import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Printer, FileDown } from 'lucide-react';
import ZoomControls from './ZoomControls';

export interface DocItem {
  id: string;    // slug used as navigation key
  title: string;
  order_index: number;
  content: string;
}

interface DocNavProps {
  org: string;
  collection: string;
  collectionName: string;
  docs: DocItem[];
  currentDocId?: string;
  zoom: number;
  onZoomChange: (zoom: number) => void;
}

export default function DocNav({
  org,
  collection,
  docs,
  currentDocId,
  zoom,
  onZoomChange,
}: DocNavProps) {
  const navigate = useNavigate();

  const sortedDocs = useMemo(() => {
    return [...docs].sort((a, b) => a.order_index - b.order_index);
  }, [docs]);

  const handlePrint = () => {
    window.print();
  };

  const navigateToDoc = (slug: string) => {
    navigate(`/${org}/${collection}/${slug}`);
  };

  return (
    <aside className="w-72 bg-card border-r border-border flex flex-col h-[calc(100vh-3.5rem)] sticky top-14 no-print">
      {/* Document List */}
      <nav className="flex-1 overflow-y-auto p-2">
        <ul className="space-y-0.5">
          {sortedDocs.map((doc) => {
            const isActive = doc.id === currentDocId;
            const displayIndex = sortedDocs.findIndex((d) => d.id === doc.id) + 1;

            return (
              <li key={doc.id}>
                <button
                  onClick={() => navigateToDoc(doc.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-start gap-2 transition-colors ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <span className={`font-mono text-xs min-w-[1.5rem] ${isActive ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                    {displayIndex}.
                  </span>
                  <span className="flex-1 line-clamp-2">{doc.title}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Controls Section */}
      <div className="border-t border-border p-4 space-y-4">
        <ZoomControls zoom={zoom} onZoomChange={onZoomChange} />
        <button
          onClick={handlePrint}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
        >
          <Printer className="w-4 h-4" />
          Print Document
        </button>
        {sortedDocs.length > 1 && (
          <Link
            to={`/${org}/${collection}/pdf`}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
          >
            <FileDown className="w-4 h-4" />
            Export as PDF
          </Link>
        )}
      </div>
    </aside>
  );
}
